import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    Container,
    Avatar,
    Paper,
    useTheme,
    alpha,
    IconButton,
    Collapse,
    Tooltip,
    Snackbar,
    Alert,
    Menu,
    MenuItem,
    Button,
} from '@mui/material';
import { ChevronLeft, ChevronRight, ExpandMore, ExpandLess, Check, Close } from '@mui/icons-material';
import { LoadingScreen } from './shared/Loading';
import { collection, query, where, onSnapshot, orderBy, doc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Plant, Space, PlantingSuggestion } from '../types/shared';
import { useTranslation } from 'react-i18next';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase';

// Shared types for timeline items
interface TimelineItem {
    id: string;
    name: string;
    variety?: string;
    imageUrl?: string;
    spaceId?: string;
    targetSpaceId?: string;
    count?: number;
    stage?: string;
    // Timeline dates
    seedlingStart?: Date | null;
    transplantDate?: Date | null;
    plantingDate?: Date | null;
    harvestStart?: Date | null;
    harvestEnd?: Date | null;
    // Suggestion-specific
    isSuggestion: boolean;
    suggestionId?: string;
    reason?: string;
    // Projection: seedling shown in current space, growth/harvest in target space
    seedlingOnly?: boolean;       // true = only show seedling bar (in current space)
    isProjection?: boolean;       // true = only show growth+harvest bars (in target space)
    projectionSourceSpace?: string; // Name of the space where the seedling currently lives
}

function toDate(val: any): Date | null {
    if (!val) return null;
    if (typeof val === 'string') {
        const d = new Date(val);
        return isNaN(d.getTime()) ? null : d;
    }
    if (typeof val.toDate === 'function') return val.toDate();
    if (typeof val.seconds === 'number') return new Date(val.seconds * 1000);
    if (val instanceof Date) return val;
    return null;
}

export default function GardenPlanPage() {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const [plants, setPlants] = useState<Plant[]>([]);
    const [spaces, setSpaces] = useState<Space[]>([]);
    const [suggestions, setSuggestions] = useState<PlantingSuggestion[]>([]);
    const [loading, setLoading] = useState(true);
    const [collapsedSpaces, setCollapsedSpaces] = useState<Record<string, boolean>>({});
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
    const bodyScrollRef = useRef<HTMLDivElement>(null);
    const headerMonthsRef = useRef<HTMLDivElement>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [activePlantId, setActivePlantId] = useState<string | null>(null);
    const isSyncing = useRef(false);

    // Timeline configuration
    const today = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    const startDate = useMemo(() => {
        const d = new Date(today);
        d.setDate(1);
        return d;
    }, [today]);

    const totalDays = 365;
    const dayWidth = 3;
    const contentWidth = 250 + totalDays * dayWidth;

    // Data fetching
    useEffect(() => {
        const unsubscribeAuth = auth.onAuthStateChanged((user) => {
            if (user) {
                // Spaces — real-time
                const spacesQuery = query(
                    collection(db, 'spaces'),
                    where('userId', '==', user.uid),
                    orderBy('order', 'asc')
                );
                const unsubSpaces = onSnapshot(spacesQuery, (snap) => {
                    setSpaces(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Space)));
                });

                // Plants — real-time
                const plantsQuery = query(
                    collection(db, 'plants'),
                    where('userId', '==', user.uid),
                    where('stage', 'in', ['Seedling', 'Plant'])
                );
                const unsubPlants = onSnapshot(plantsQuery, (snap) => {
                    setPlants(
                        snap.docs
                            .map(doc => ({ id: doc.id, ...doc.data() } as Plant))
                            .filter(p => (p.count ?? 1) > 0)
                    );
                    setLoading(false);
                });

                // Suggestions — real-time
                const suggestionsQuery = query(
                    collection(db, 'planting_suggestions'),
                    where('userId', '==', user.uid),
                    where('status', '==', 'pending')
                );
                const unsubSuggestions = onSnapshot(suggestionsQuery, (snap) => {
                    setSuggestions(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as PlantingSuggestion)));
                });

                return () => {
                    unsubSpaces();
                    unsubPlants();
                    unsubSuggestions();
                };
            } else {
                setPlants([]);
                setSpaces([]);
                setSuggestions([]);
                setLoading(false);
            }
        });

        return () => unsubscribeAuth();
    }, []);

    // Scroll sync
    useEffect(() => {
        const bodyEl = bodyScrollRef.current;
        if (!bodyEl) return;

        const handleBodyScroll = () => {
            if (isSyncing.current) return;
            isSyncing.current = true;
            requestAnimationFrame(() => {
                if (headerMonthsRef.current) {
                    headerMonthsRef.current.style.transform = `translateX(-${bodyEl.scrollLeft}px)`;
                }
                isSyncing.current = false;
            });
        };

        bodyEl.addEventListener('scroll', handleBodyScroll, { passive: true });
        return () => bodyEl.removeEventListener('scroll', handleBodyScroll);
    }, [loading, plants.length, suggestions.length]);

    // Initialize scroll to today
    useEffect(() => {
        const todayDiffDays = Math.max(0, (today.getTime() - startDate.getTime()) / (1000 * 3600 * 24));
        const scrollPos = (todayDiffDays * dayWidth) - 50;
        if (bodyScrollRef.current) {
            bodyScrollRef.current.scrollLeft = scrollPos;
        }
        if (headerMonthsRef.current) {
            headerMonthsRef.current.style.transform = `translateX(-${scrollPos}px)`;
        }
    }, [loading, plants.length, suggestions.length, today, startDate, dayWidth]);

    const handleScroll = useCallback((direction: 'left' | 'right') => {
        if (bodyScrollRef.current) {
            const scrollAmount = dayWidth * 30;
            bodyScrollRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    }, [dayWidth]);

    // Suggestion actions
    const handleSuggestionAction = useCallback(async (suggestionId: string, status: 'accepted' | 'rejected') => {
        try {
            const functions = getFunctions(undefined, 'europe-west1');
            const updateFn = httpsCallable(functions, 'updateSuggestionStatus');
            await updateFn({ suggestionId, status });
            setSnackbar({
                open: true,
                message: status === 'accepted' ? t('harvest.accept') : t('harvest.reject'),
                severity: 'success'
            });
            if (analytics) {
                logEvent(analytics, 'suggestion_action', { action: status, suggestionId });
            }
        } catch (error) {
            console.error('Error updating suggestion:', error);
            setSnackbar({ open: true, message: 'Error', severity: 'error' });
        }
    }, [t]);

    // Toggle space collapse
    const toggleSpace = useCallback((spaceId: string) => {
        setCollapsedSpaces(prev => ({ ...prev, [spaceId]: !prev[spaceId] }));
    }, []);

    const handleOpenSpaceMenu = useCallback((event: React.MouseEvent<HTMLElement>, plantId: string) => {
        setAnchorEl(event.currentTarget);
        setActivePlantId(plantId);
    }, []);

    const handleCloseSpaceMenu = useCallback(() => {
        setAnchorEl(null);
        setActivePlantId(null);
    }, []);

    const handleSelectSpace = useCallback(async (newSpaceId: string) => {
        if (!activePlantId) return;
        try {
            await updateDoc(doc(db, 'plants', activePlantId), {
                targetSpaceId: newSpaceId,
                updatedAt: new Date()
            });
            handleCloseSpaceMenu();
            setSnackbar({ open: true, message: t('plants.target_space_updated', 'Target space updated'), severity: 'success' });
            if (analytics) {
                logEvent(analytics, 'target_space_updated', { plant_id: activePlantId, space_id: newSpaceId });
            }
        } catch (error) {
            console.error('Error updating target space:', error);
            setSnackbar({ open: true, message: 'Error', severity: 'error' });
        }
    }, [activePlantId, t, analytics]);

    // Build timeline items grouped by space
    const { spaceGroups, hasItems } = useMemo(() => {
        const groups: { space: Space | null; items: TimelineItem[] }[] = [];

        // Build a map of spaceId -> items
        const spaceItemMap: Record<string, TimelineItem[]> = {};
        const unassignedItems: TimelineItem[] = [];

        // Build a space name lookup
        const spaceNameMap: Record<string, string> = {};
        for (const sp of spaces) { spaceNameMap[sp.id] = sp.name; }

        // Add plants
        for (const plant of plants) {
            const hasDifferentTarget = plant.stage === 'Seedling'
                && plant.targetSpaceId
                && plant.targetSpaceId !== plant.spaceId;

            const item: TimelineItem = {
                id: plant.id,
                name: plant.name,
                variety: plant.variety,
                imageUrl: plant.imageUrl,
                spaceId: plant.spaceId,
                targetSpaceId: plant.targetSpaceId,
                count: plant.count,
                stage: plant.stage,
                seedlingStart: toDate(plant.seedlingStartDate || (plant.stage === 'Seedling' || plant.transplantDate ? plant.plantingDate : null)),
                transplantDate: toDate(plant.transplantDate),
                plantingDate: toDate(plant.plantingDate),
                harvestStart: toDate(plant.harvestForecastStart),
                harvestEnd: toDate(plant.harvestForecastEnd),
                isSuggestion: false,
                seedlingOnly: false, // Show bars even for seedlings with target space
            };

            const key = plant.spaceId || '__unassigned__';
            if (key === '__unassigned__') {
                unassignedItems.push(item);
            } else {
                (spaceItemMap[key] = spaceItemMap[key] || []).push(item);
            }

            // Create a projection in the target space (growth + harvest only)
            if (hasDifferentTarget) {
                const projection: TimelineItem = {
                    id: `${plant.id}-projection`,
                    name: plant.name,
                    variety: plant.variety,
                    imageUrl: plant.imageUrl,
                    spaceId: plant.targetSpaceId,
                    count: plant.count,
                    stage: plant.stage,
                    seedlingStart: toDate(plant.seedlingStartDate || plant.plantingDate),
                    transplantDate: toDate(plant.transplantDate),
                    plantingDate: toDate(plant.plantingDate),
                    harvestStart: toDate(plant.harvestForecastStart),
                    harvestEnd: toDate(plant.harvestForecastEnd),
                    isSuggestion: false,
                    isProjection: true,
                    projectionSourceSpace: spaceNameMap[plant.spaceId || ''] || '',
                };
                const targetKey = plant.targetSpaceId!;
                (spaceItemMap[targetKey] = spaceItemMap[targetKey] || []).push(projection);
            }
        }

        // Add suggestions (filter out any with past actionable dates as a safety net)
        const todayStr = today.toISOString().slice(0, 10); // YYYY-MM-DD
        const activeSuggestions = suggestions.filter(sugg => {
            const actionableDate = sugg.transplantDate || sugg.suggestedPlantDate;
            return !actionableDate || actionableDate >= todayStr;
        });
        for (const sugg of activeSuggestions) {
            const item: TimelineItem = {
                id: `suggestion-${sugg.id}`,
                name: sugg.seedName,
                variety: sugg.seedVariety,
                imageUrl: sugg.imageUrl,
                spaceId: sugg.suggestedSpaceId,
                stage: sugg.seedlingStartDate ? 'Seedling' : 'Plant',
                seedlingStart: sugg.seedlingStartDate ? toDate(sugg.seedlingStartDate) : null,
                transplantDate: sugg.transplantDate ? toDate(sugg.transplantDate) : null,
                plantingDate: toDate(sugg.suggestedPlantDate),
                harvestStart: toDate(sugg.forecastedHarvestStart),
                harvestEnd: toDate(sugg.forecastedHarvestEnd),
                isSuggestion: true,
                suggestionId: sugg.id,
                reason: sugg.reason,
            };

            const key = sugg.suggestedSpaceId || '__unassigned__';
            if (key === '__unassigned__') {
                unassignedItems.push(item);
            } else {
                (spaceItemMap[key] = spaceItemMap[key] || []).push(item);
            }
        }

        // Sort items within each space by priority and harvest date
        const sortItems = (items: TimelineItem[]) => {
            const getPriority = (item: TimelineItem) => {
                if (item.isSuggestion) return 3;
                if (item.isProjection) return 2;
                if (item.stage === 'Seedling') return 0;
                return 1;
            };

            items.sort((a, b) => {
                const priorityA = getPriority(a);
                const priorityB = getPriority(b);
                if (priorityA !== priorityB) return priorityA - priorityB;

                const aDate = a.harvestStart?.getTime() ?? Number.MAX_SAFE_INTEGER;
                const bDate = b.harvestStart?.getTime() ?? Number.MAX_SAFE_INTEGER;
                return aDate - bDate;
            });
        };

        // Build groups in space order
        for (const space of spaces) {
            const items = spaceItemMap[space.id] || [];
            sortItems(items);
            if (items.length > 0) {
                groups.push({ space, items });
            }
        }

        // Add unassigned group
        if (unassignedItems.length > 0) {
            sortItems(unassignedItems);
            groups.push({ space: null, items: unassignedItems });
        }

        const totalItems = groups.reduce((sum, g) => sum + g.items.length, 0);
        return { spaceGroups: groups, hasItems: totalItems > 0 };
    }, [plants, suggestions, spaces]);

    if (loading) {
        return <LoadingScreen />;
    }

    const daysArray = Array.from({ length: totalDays }).map((_, i) => {
        const d = new Date(startDate);
        d.setDate(d.getDate() + i);
        return d;
    });

    const isToday = (d: Date) => d.getTime() === today.getTime();

    const getBarStyles = (start: Date | null, end: Date | null) => {
        if (!start || !end) return { display: 'none' as const, clippedStart: false, clippedEnd: false };
        const s = new Date(start); s.setHours(0, 0, 0, 0);
        const e = new Date(end); e.setHours(0, 0, 0, 0);
        const lastDay = daysArray[daysArray.length - 1];
        if (e.getTime() < startDate.getTime() || s.getTime() > lastDay.getTime()) {
            return { display: 'none' as const, clippedStart: false, clippedEnd: false };
        }
        const clippedStart = s.getTime() < startDate.getTime();
        const clippedEnd = e.getTime() > lastDay.getTime();
        const startDiffDays = Math.max(0, (s.getTime() - startDate.getTime()) / (1000 * 3600 * 24));
        const endDiffDays = Math.min(totalDays, (e.getTime() - startDate.getTime()) / (1000 * 3600 * 24));
        const widthDays = endDiffDays - startDiffDays + 1;
        if (widthDays <= 0) return { display: 'none' as const, clippedStart: false, clippedEnd: false };
        return {
            left: `${startDiffDays * dayWidth}px`,
            width: `${widthDays * dayWidth}px`,
            clippedStart,
            clippedEnd,
        };
    };

    // Month header cells
    const renderHeaderCells = () => daysArray.map((date, i) => (
        <Box
            key={i}
            sx={{
                width: dayWidth,
                minWidth: dayWidth,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderLeft: date.getDate() === 1 ? '1px solid rgba(0,0,0,0.2)' : 'none',
                bgcolor: isToday(date) ? alpha(theme.palette.primary.main, 0.1) : (date.getMonth() % 2 === 0 ? '#f8fafc' : '#fff'),
                position: 'relative',
                overflow: date.getDate() === 1 ? 'visible' : 'hidden',
                zIndex: date.getDate() === 1 ? 2 : 0,
            }}
        >
            {date.getDate() === 1 && (
                <Typography
                    variant="caption"
                    sx={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        fontSize: '0.7rem',
                        color: 'text.secondary',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {date.toLocaleString(i18n.language || 'default', { month: 'long', ...(date.getMonth() === 0 ? { year: 'numeric' } : {}) })}
                </Typography>
            )}
        </Box>
    ));

    // Render a single timeline row
    const renderTimelineRow = (item: TimelineItem) => {
        // Determine bar phases
        const seedlingEnd = item.transplantDate || item.plantingDate;
        const growthStart = item.transplantDate || item.plantingDate;

        // Helper to subtract 1 day to prevent overlap
        const getPreviousDay = (date: Date | null): Date | null => {
            if (!date) return null;
            const d = new Date(date);
            d.setDate(d.getDate() - 1);
            return d;
        };

        return (
            <Box key={item.id} sx={{
                display: 'flex',
                height: 48,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                opacity: item.isSuggestion ? 0.6 : 1,
                transition: 'opacity 0.2s',
                '&:hover': item.isSuggestion ? { opacity: 0.9 } : {},
            }}>
                {/* Left sticky cell */}
                <Box sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 5,
                    width: 250,
                    minWidth: 250,
                    p: 1.5,
                    pl: 2,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    bgcolor: item.isSuggestion ? '#f0faf0' : item.isProjection ? '#f8faf8' : '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 1,
                }}>
                    <Avatar
                        variant="rounded"
                        src={item.imageUrl || undefined}
                        sx={{
                            width: 32, height: 32,
                            bgcolor: item.isSuggestion ? 'success.light' : 'primary.light',
                            borderRadius: 1,
                            border: item.isSuggestion ? '2px dashed' : item.isProjection ? '2px dashed' : 'none',
                            borderColor: item.isSuggestion ? 'success.main' : item.isProjection ? '#4caf50' : undefined,
                            opacity: item.isProjection ? 0.8 : 1,
                        }}
                    >
                        {item.name?.charAt(0)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                        <Typography variant="body2" component="h3" className="serif-text" fontWeight="600" noWrap sx={{ mb: 0 }}>
                            {item.name}{item.variety ? ` (${item.variety})` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {item.count ? `${item.count}x ` : ''}
                            {item.isSuggestion ? t('harvest.suggestions') :
                                item.isProjection ? `↗ ${item.projectionSourceSpace}` :
                                (item.stage === 'Seedling' ? t('plants.stage_seedling', 'Seedling') : t('plants.stage_plant', 'Plant'))
                            }
                        </Typography>
                    </Box>
                    {/* Suggestion actions */}
                    {item.isSuggestion && item.suggestionId && (
                        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                            <Tooltip title={item.reason || t('harvest.accept')}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleSuggestionAction(item.suggestionId!, 'accepted')}
                                    sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.25) } }}
                                >
                                    <Check sx={{ fontSize: 16, color: 'success.main' }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('harvest.reject')}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleSuggestionAction(item.suggestionId!, 'rejected')}
                                    sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.25) } }}
                                >
                                    <Close sx={{ fontSize: 16, color: 'error.main' }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Box>

                {/* Right: timeline bars */}
                <Box sx={{ display: 'flex', position: 'relative', flex: 1 }}>
                    {daysArray.map((date, i) => (
                        <Box
                            key={i}
                            sx={{
                                width: dayWidth,
                                minWidth: dayWidth,
                                height: '100%',
                                borderLeft: date.getDate() === 1 ? '1px solid rgba(0,0,0,0.1)' : 'none',
                                bgcolor: isToday(date) ? alpha(theme.palette.primary.main, 0.05) : (date.getMonth() % 2 === 0 ? '#f5f5f5' : '#fff'),
                                position: 'relative'
                            }}
                        >
                            {isToday(date) && (
                                <Box sx={{ position: 'absolute', top: 0, bottom: 0, width: 2, bgcolor: 'primary.main', zIndex: 0 }} />
                            )}
                        </Box>
                    ))}

                    {/* Seedling phase bar (light green) */}
                    {(() => {
                        if (!item.seedlingStart || !seedlingEnd) return null;
                        // Use day before transplant for seedling phase end to avoid overlap
                        const adjustedEnd = getPreviousDay(seedlingEnd);
                        if (!adjustedEnd || adjustedEnd < item.seedlingStart) return null;

                        const { clippedStart, clippedEnd, ...styles } = getBarStyles(item.seedlingStart, adjustedEnd);
                        const color = '#81c784';
                        const isElsewhere = !!item.isProjection;
                        
                        return (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 14,
                                    bottom: 14,
                                    bgcolor: isElsewhere ? 'transparent' : (item.isSuggestion ? alpha(color, 0.5) : color),
                                    border: isElsewhere ? `3px solid ${color}` : 'none',
                                    boxSizing: 'border-box',
                                    borderRadius: `${clippedStart ? 0 : 4}px ${clippedEnd ? 0 : 0}px ${clippedEnd ? 0 : 0}px ${clippedStart ? 0 : 4}px`,
                                    zIndex: 1,
                                    ...styles,
                                }}
                            />
                        );
                    })()}

                    {/* Growth phase bar (green) — hidden for seedling-only entries */}
                    {(() => {
                        if (item.seedlingOnly) return null;
                        if (!growthStart || !item.harvestStart) return null;
                        
                        // Use day before harvest for growth phase end to avoid overlap
                        const adjustedEnd = getPreviousDay(item.harvestStart);
                        if (!adjustedEnd || adjustedEnd < growthStart) return null;

                        const { clippedStart, clippedEnd, ...styles } = getBarStyles(growthStart, adjustedEnd);
                        const color = '#4caf50';
                        const isElsewhere = !item.isProjection && !!item.targetSpaceId && item.targetSpaceId !== item.spaceId;
                        const hasLeftCap = !item.seedlingStart && !clippedStart;

                        return (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 12,
                                    bottom: 12,
                                    bgcolor: isElsewhere ? 'transparent' : (item.isSuggestion ? alpha(color, 0.5) : (item.isProjection ? alpha(color, 0.6) : color)),
                                    border: isElsewhere ? `3px solid ${color}` : 'none',
                                    boxSizing: 'border-box',
                                    borderRadius: `${hasLeftCap ? 4 : 0}px ${clippedEnd ? 0 : 0}px ${clippedEnd ? 0 : 0}px ${hasLeftCap ? 4 : 0}px`,
                                    zIndex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    ...styles,
                                }}
                            >
                                {item.stage === 'Seedling' && !item.isSuggestion && !item.isProjection && (
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={(e) => handleOpenSpaceMenu(e, item.id)}
                                        sx={{
                                            color: isElsewhere ? color : '#fff',
                                            fontSize: '0.65rem',
                                            minWidth: 0,
                                            px: 1,
                                            py: 0,
                                            fontWeight: 'bold',
                                            textTransform: 'none',
                                            textShadow: isElsewhere ? 'none' : '0 0 2px rgba(0,0,0,0.5)',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                                        }}
                                    >
                                        {item.targetSpaceId
                                            ? spaces.find(s => s.id === item.targetSpaceId)?.name || '...'
                                            : t('harvest.set_target', 'Set Target')}
                                    </Button>
                                )}
                            </Box>
                        );
                    })()}

                    {/* Harvest window bar (gold) — hidden for seedling-only entries */}
                    {(() => {
                        if (item.seedlingOnly) return null;
                        if (!item.harvestStart || !item.harvestEnd) return null;
                        const { clippedStart, clippedEnd, ...styles } = getBarStyles(item.harvestStart, item.harvestEnd);
                        const color = 'rgb(232, 201, 10)';
                        const isElsewhere = !item.isProjection && !!item.targetSpaceId && item.targetSpaceId !== item.spaceId;

                        return (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 10,
                                    bottom: 10,
                                    bgcolor: isElsewhere ? 'transparent' : (item.isSuggestion ? alpha(color, 0.5) : (item.isProjection ? alpha(color, 0.6) : color)),
                                    border: isElsewhere ? `3px solid ${color}` : 'none',
                                    boxSizing: 'border-box',
                                    borderRadius: `${clippedStart ? 0 : 0}px ${clippedEnd ? 0 : 4}px ${clippedEnd ? 0 : 4}px ${clippedStart ? 0 : 0}px`,
                                    zIndex: 1,
                                    ...styles,
                                }}
                            />
                        );
                    })()}
                </Box>
            </Box>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3, pb: 2 }}>
            {!hasItems ? (
                <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} px={1}>
                        <Typography variant="h5" fontWeight="800" color="primary.main">
                            {t('harvest.title', 'Garden Plan')}
                        </Typography>
                    </Box>
                    <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 4 }}>
                        <Typography color="text.secondary">{t('harvest.no_plants', 'No active plants or suggestions yet.')}</Typography>
                    </Paper>
                </>
            ) : (
                <>
                    {/* Legend */}
                    <Box display="flex" gap={2} mb={1.5} px={1} flexWrap="wrap">
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <Box sx={{ width: 16, height: 8, bgcolor: '#81c784', borderRadius: 0.5 }} />
                            <Typography variant="caption" color="text.secondary">{t('harvest.seedling_phase', 'Seedling')}</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <Box sx={{ width: 16, height: 8, bgcolor: '#4caf50', borderRadius: 0.5 }} />
                            <Typography variant="caption" color="text.secondary">{t('harvest.growth_phase', 'Growth')}</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <Box sx={{ width: 16, height: 8, bgcolor: 'rgb(232, 201, 10)', borderRadius: 0.5 }} />
                            <Typography variant="caption" color="text.secondary">{t('harvest.harvest_window', 'Harvest')}</Typography>
                        </Box>
                    </Box>

                    {/* Sticky section: title + nav + month header */}
                    <Box
                        sx={{
                            position: 'sticky',
                            top: { xs: 56, sm: 64 },
                            zIndex: 12,
                            bgcolor: '#f8fafc',
                            borderRadius: '16px 16px 0 0',
                            border: '1px solid rgba(0,0,0,0.05)',
                            borderBottom: 'none',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Title + navigation buttons */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1.5}>
                            <Typography variant="h5" fontWeight="800" color="primary.main">
                                {t('harvest.title', 'Garden Plan')}
                            </Typography>
                            <Box>
                                <IconButton onClick={() => handleScroll('left')} size="small" sx={{ mr: 1, bgcolor: 'background.paper', boxShadow: 1 }}>
                                    <ChevronLeft />
                                </IconButton>
                                <IconButton onClick={() => handleScroll('right')} size="small" sx={{ bgcolor: 'background.paper', boxShadow: 1 }}>
                                    <ChevronRight />
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Month header row */}
                        <Box sx={{ position: 'relative', height: 36, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{
                                position: 'absolute', left: 0, top: 0, bottom: 0, width: 250,
                                zIndex: 3, p: 2, borderRight: '1px solid', borderColor: 'divider',
                                bgcolor: '#f8fafc', display: 'flex', alignItems: 'center'
                            }}>
                                <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                                    {t('harvest.plant', 'Plant')}
                                </Typography>
                            </Box>
                            <Box sx={{ position: 'absolute', left: 250, top: 0, bottom: 0, right: 0, overflow: 'hidden' }}>
                                <Box
                                    ref={headerMonthsRef}
                                    sx={{ display: 'flex', height: '100%', width: totalDays * dayWidth, willChange: 'transform' }}
                                >
                                    {renderHeaderCells()}
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Scrollable body */}
                    <Box
                        ref={bodyScrollRef}
                        sx={{
                            overflowX: 'auto',
                            '&::-webkit-scrollbar': { display: 'none' },
                            msOverflowStyle: 'none',
                            scrollbarWidth: 'none',
                            border: '1px solid rgba(0,0,0,0.05)',
                            borderTop: 'none',
                            borderRadius: '0 0 16px 16px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.04)',
                        }}
                    >
                        <Box sx={{ width: contentWidth }}>
                            {spaceGroups.map((group) => {
                                const spaceId = group.space?.id || '__unassigned__';
                                const spaceName = group.space?.name || t('harvest.unassigned', 'Unassigned');
                                const isCollapsed = collapsedSpaces[spaceId] || false;
                                const focus = group.space?.focus;

                                return (
                                    <Box key={spaceId}>
                                        {/* Space header */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                py: 0.75,
                                                bgcolor: alpha(theme.palette.primary.main, 0.06),
                                                borderBottom: '1px solid',
                                                borderColor: 'divider',
                                                cursor: 'pointer',
                                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                                            }}
                                            onClick={() => toggleSpace(spaceId)}
                                        >
                                            {/* Sticky space name */}
                                            <Box sx={{
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 5,
                                                width: 250,
                                                minWidth: 250,
                                                px: 2,
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 1,
                                                bgcolor: alpha(theme.palette.primary.main, 0.06),
                                                borderRight: '1px solid',
                                                borderColor: 'divider',
                                            }}>
                                                <Box sx={{ pt: 0.25 }}>
                                                    {isCollapsed ? <ExpandMore sx={{ fontSize: 18 }} /> : <ExpandLess sx={{ fontSize: 18 }} />}
                                                </Box>
                                                <Box sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                                                    <Typography variant="subtitle2" fontWeight="700" color="primary.dark" sx={{ whiteSpace: 'normal', lineHeight: 1.2 }}>
                                                        {spaceName}
                                                    </Typography>
                                                    {focus && !isCollapsed && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', whiteSpace: 'normal', lineHeight: 1.1, mt: 0.25 }}>
                                                            {focus}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                            {/* Timeline cells for header (just bg) */}
                                            <Box sx={{ display: 'flex', flex: 1 }}>
                                                {daysArray.map((date, i) => (
                                                    <Box
                                                        key={i}
                                                        sx={{
                                                            width: dayWidth,
                                                            minWidth: dayWidth,
                                                            height: '100%',
                                                            borderLeft: date.getDate() === 1 ? '1px solid rgba(0,0,0,0.1)' : 'none',
                                                            bgcolor: isToday(date) ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>

                                        {/* Collapsible items */}
                                        <Collapse in={!isCollapsed} timeout="auto">
                                            {group.items.map(renderTimelineRow)}
                                        </Collapse>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseSpaceMenu}
                PaperProps={{
                    sx: { minWidth: 150, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }
                }}
            >
                <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', color: 'text.secondary', fontWeight: 'bold' }}>
                    {t('plants.select_target_space', 'Select Target Space')}
                </Typography>
                {spaces.map(space => (
                    <MenuItem key={space.id} onClick={() => handleSelectSpace(space.id)}>
                        {space.name}
                    </MenuItem>
                ))}
            </Menu>
        </Container>
    );
}
